import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  onSnapshot, 
  setDoc, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { paymentService } from '../services/paymentService';

interface CobrancaRecorrente {
  valor: number;
  quantidadeParcelas: number;
  dataVencimento: string;
  descricao: string;
  dataCriacao: any;
  status: 'ativa' | 'inativa';
  parcelasRestantes: number;
  parcelas: Array<{
    numero: number;
    valor: number;
    dataVencimento: any;
    status: 'pendente' | 'paga';
    dataPagamento?: any;
  }>;
  carenciaMotoboy?: number;
  carenciaEmpresa?: number;
}

interface HistoricoPagamento {
  parcelaNumero: number;
  valor: number;
  dataVencimento: any;
  dataPagamento: any;
  dataDesmarcacao?: any;
  descricao: string;
  status: 'pago' | 'desmarcado';
  dataRegistro: any;
}

const Mensalidades: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [empresaId, setEmpresaId] = useState<string>('');
  const [empresaNome, setEmpresaNome] = useState<string>('');
  const [empresaFoto, setEmpresaFoto] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cobrancaRecorrente, setCobrancaRecorrente] = useState<CobrancaRecorrente | null>(null);
  const [cobrancaRecorrenteId, setCobrancaRecorrenteId] = useState<string>('');
  const [historicoPagamentos, setHistoricoPagamentos] = useState<HistoricoPagamento[]>([]);
  const [parcelasExpandidas, setParcelasExpandidas] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  
  // Estados para pagamento
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedParcela, setSelectedParcela] = useState<any>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'error' | null>(null);
  const [paymentUnsubscribe, setPaymentUnsubscribe] = useState<(() => void) | null>(null);

  const carregarDados = async () => {
    try {
      const storedUserId = await storage.getItem('userId');
      if (!storedUserId) {
        alert('Erro: Usuário não encontrado. Faça login novamente.');
        navigate('/login');
        return;
      }

      // Buscar dados do usuário para obter o empresaId
      const userDoc = await getDoc(doc(db, 'usuarios', storedUserId));
      if (!userDoc.exists()) {
        alert('Erro: Usuário não encontrado.');
        navigate('/login');
        return;
      }

      const userData = userDoc.data();
      if (!userData.empresaId) {
        alert('Erro: Empresa não encontrada.');
        navigate('/login');
        return;
      }

      setEmpresaId(userData.empresaId);

      // Carregar nome da empresa
      const empresaDoc = await getDoc(doc(db, 'Empresa', userData.empresaId));
      if (empresaDoc.exists()) {
        const empresaData = empresaDoc.data();
        setEmpresaNome(empresaData.nomeEmpresa || empresaData.nome || '');
        setEmpresaFoto(empresaData.foto || empresaData.fotoUrl || '');
      }

      // Carregar dados da cobrança recorrente
      try {
        const cobrancaDoc = await getDoc(doc(db, 'Empresa', userData.empresaId, 'cobrancasRecorrentes', 'ativa'));
        
        if (cobrancaDoc.exists()) {
          const cobrancaData = cobrancaDoc.data() as CobrancaRecorrente;
          setCobrancaRecorrente(cobrancaData);
          setCobrancaRecorrenteId('ativa');
          
          // Carregar histórico de pagamentos
          const historicoDoc = await getDoc(doc(db, 'Empresa', userData.empresaId, 'cobrancasRecorrentes', 'historico'));
          if (historicoDoc.exists()) {
            setHistoricoPagamentos(historicoDoc.data().pagamentos || []);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar cobrança:', error);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro: Não foi possível carregar os dados das cobranças.');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await carregarDados();
    setRefreshing(false);
  };

  const calcularStatusParcela = (parcela: any) => {
    if (parcela.status === 'paga') return 'paga';
    
    const hoje = new Date();
    const dataVencimento = parcela.dataVencimento?.toDate ? parcela.dataVencimento.toDate() : new Date(parcela.dataVencimento);
    
    const diffTime = dataVencimento.getTime() - hoje.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'atrasada';
    if (diffDays === 0) return 'vencendo';
    return 'pendente';
  };

  const formatarData = (data: any) => {
    if (!data) return 'Data não disponível';
    const dataObj = data?.toDate ? data.toDate() : new Date(data);
    return format(dataObj, 'dd/MM/yyyy', { locale: ptBR });
  };

  const formatarMoeda = (valor: number) => {
    if (valor === undefined || valor === null || isNaN(valor)) {
      return 'R$ 0,00';
    }
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paga':
        return '#4CAF50';
      case 'atrasada':
        return '#F44336';
      case 'vencendo':
        return '#FF9800';
      default:
        return '#1976D2';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paga':
        return 'Paga';
      case 'atrasada':
        return 'Atrasada';
      case 'vencendo':
        return 'Vence Hoje';
      default:
        return 'Pendente';
    }
  };

  const podePagarParcela = (parcela: any) => {
    const status = calcularStatusParcela(parcela);
    return status === 'pendente' || status === 'atrasada' || status === 'vencendo';
  };

  const atualizarParcelaComoPaga = async (parcela: any) => {
    try {
      if (!parcela || !parcela.numero) {
        throw new Error('Parcela inválida');
      }

      const cobrancaRef = doc(db, 'Empresa', empresaId, 'cobrancasRecorrentes', 'ativa');
      const cobrancaDoc = await getDoc(cobrancaRef);
      
      if (!cobrancaDoc.exists()) {
        throw new Error('Cobrança recorrente não encontrada');
      }

      const cobrancaData = cobrancaDoc.data() as CobrancaRecorrente;
      const parcelasAtualizadas = cobrancaData.parcelas.map((p: any) => {
        const numeroParcela = Number(p.numero);
        const numeroParcelaSelecionada = Number(parcela.numero);
        
        if (numeroParcela === numeroParcelaSelecionada) {
          return {
            ...p,
            status: 'paga',
            dataPagamento: new Date()
          };
        }
        return p;
      });

      const parcelasRestantes = parcelasAtualizadas.filter((p: any) => p.status === 'pendente').length;

      await updateDoc(cobrancaRef, {
        parcelas: parcelasAtualizadas,
        parcelasRestantes: parcelasRestantes
      });

      // Adicionar ao histórico de pagamentos
      const historicoRef = doc(db, 'Empresa', empresaId, 'cobrancasRecorrentes', 'historico');
      const historicoDoc = await getDoc(historicoRef);
      
      const novoPagamento = {
        parcelaNumero: Number(parcela.numero),
        valor: Number(parcela.valor),
        dataVencimento: parcela.dataVencimento,
        dataPagamento: new Date(),
        descricao: cobrancaData.descricao,
        status: 'pago',
        dataRegistro: new Date()
      };

      if (historicoDoc.exists()) {
        const historicoData = historicoDoc.data();
        const pagamentos = historicoData.pagamentos || [];
        pagamentos.unshift(novoPagamento);
        
        await updateDoc(historicoRef, {
          pagamentos: pagamentos
        });
      } else {
        await setDoc(historicoRef, {
          pagamentos: [novoPagamento]
        });
      }

      return true;
    } catch (error) {
      console.error('Erro ao atualizar parcela como paga:', error);
      throw error;
    }
  };

  const handlePagarParcela = async (parcela: any) => {
    if (!empresaId) {
      alert('Erro: Empresa não identificada. Faça login novamente.');
      return;
    }

    setSelectedParcela(parcela);
    setPaymentLoading(true);
    setShowPaymentModal(true);
    setPaymentStatus(null);

    try {
      const paymentData = {
        empresaId: empresaId,
        valor: parcela.valor,
        descricao: `Parcela ${parcela.numero} - ${cobrancaRecorrente?.descricao || 'Cobrança Recorrente'}`,
        parcelaId: parcela.numero.toString(),
        cobrancaRecorrenteId: cobrancaRecorrenteId,
        userId: empresaId
      };

      const preference = await paymentService.createPaymentPreference(paymentData);
      
      if (preference && preference.init_point) {
        setPaymentUrl(preference.init_point);
        
        // Abrir URL do pagamento em nova aba
        window.open(preference.init_point, '_blank');
        
        // Monitorar status do pagamento
        const parcelaAtual = parcela;
        const unsubscribe = await paymentService.watchPaymentStatus(preference.id, {
          onPending: () => {
            setPaymentStatus('pending');
          },
          onApproved: async () => {
            setPaymentStatus('success');
            setPaymentLoading(false);
            
            try {
              if (parcelaAtual && parcelaAtual.numero) {
                await atualizarParcelaComoPaga(parcelaAtual);
                await carregarDados(); // Recarregar dados
              }
            } catch (error) {
              console.error('Erro ao atualizar parcela:', error);
              alert('Aviso: Pagamento aprovado, mas houve um problema ao atualizar o status da parcela. Entre em contato com o suporte.');
            }
            
            setTimeout(() => {
              setShowPaymentModal(false);
              setPaymentStatus(null);
              if (paymentUnsubscribe) {
                paymentUnsubscribe();
                setPaymentUnsubscribe(null);
              }
              alert('Sucesso! Pagamento aprovado com sucesso! A parcela foi marcada como paga.');
            }, 2000);
          },
          onRejected: () => {
            setPaymentStatus('error');
            setPaymentLoading(false);
            
            setTimeout(() => {
              setShowPaymentModal(false);
              setPaymentStatus(null);
              if (paymentUnsubscribe) {
                paymentUnsubscribe();
                setPaymentUnsubscribe(null);
              }
              alert('Erro: Pagamento foi rejeitado. Tente novamente.');
            }, 2000);
          },
          onError: (error) => {
            console.error('Erro no pagamento:', error);
            setPaymentStatus('error');
            setPaymentLoading(false);
            
            setTimeout(() => {
              setShowPaymentModal(false);
              setPaymentStatus(null);
              if (paymentUnsubscribe) {
                paymentUnsubscribe();
                setPaymentUnsubscribe(null);
              }
              alert('Erro: Ocorreu um erro durante o pagamento.');
            }, 2000);
          }
        });

        setPaymentUnsubscribe(() => unsubscribe);
      } else {
        throw new Error('Não foi possível criar o pagamento');
      }

    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      alert('Erro: Não foi possível processar o pagamento. Tente novamente.');
      setShowPaymentModal(false);
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  // Listener em tempo real para atualizar quando a parcela for paga
  useEffect(() => {
    if (!empresaId) return;
    
    const cobrancaRef = doc(db, 'Empresa', empresaId, 'cobrancasRecorrentes', 'ativa');
    const unsubscribe = onSnapshot(cobrancaRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data() as CobrancaRecorrente;
        setCobrancaRecorrente(data);
      }
    });

    return () => unsubscribe();
  }, [empresaId]);

  // Limpar unsubscribe ao desmontar
  useEffect(() => {
    return () => {
      if (paymentUnsubscribe) {
        paymentUnsubscribe();
      }
    };
  }, [paymentUnsubscribe]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!cobrancaRecorrente) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              <button
                onClick={() => navigate('/empresa')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Mensalidades</h1>
                <p className="text-sm text-gray-500">Cobranças recorrentes</p>
              </div>
              <div className="w-10"></div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center py-20">
            <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Nenhuma Cobrança Configurada</h2>
            <p className="text-gray-500 text-center max-w-md">
              Sua empresa não possui cobranças recorrentes configuradas no momento.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMenu(true)}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Mensalidades</h1>
                <p className="text-sm text-gray-500">Cobranças recorrentes</p>
              </div>
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg className={`w-6 h-6 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
        {/* Card Principal da Cobrança */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h2 className="text-xl font-bold">{cobrancaRecorrente.descricao}</h2>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                cobrancaRecorrente.status === 'ativa' 
                  ? 'bg-green-500/20 text-green-100' 
                  : 'bg-yellow-500/20 text-yellow-100'
              }`}>
                {cobrancaRecorrente.status === 'ativa' ? 'Ativa' : 'Inativa'}
              </span>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Valor Mensal</p>
                <p className="text-2xl font-bold text-gray-900">{formatarMoeda(cobrancaRecorrente.valor)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Parcelas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {cobrancaRecorrente.parcelasRestantes} de {cobrancaRecorrente.quantidadeParcelas}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Criada em</p>
                <p className="text-lg font-semibold text-gray-900">{formatarData(cobrancaRecorrente.dataCriacao)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <p className={`text-lg font-semibold ${
                  cobrancaRecorrente.status === 'ativa' ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {cobrancaRecorrente.status === 'ativa' ? 'Ativa' : 'Inativa'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Seção de Parcelas */}
        {cobrancaRecorrente.parcelas && cobrancaRecorrente.parcelas.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Parcelas ({cobrancaRecorrente.parcelas.length})
              </h3>
              {cobrancaRecorrente.parcelas.length > 3 && (
                <button
                  onClick={() => setParcelasExpandidas(!parcelasExpandidas)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-semibold flex items-center gap-2"
                >
                  {parcelasExpandidas ? 'Ocultar' : 'Ver todas'}
                  <svg className={`w-4 h-4 transition-transform ${parcelasExpandidas ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>

            {!parcelasExpandidas && cobrancaRecorrente.parcelas.length > 3 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-800">
                  Mostrando 3 de {cobrancaRecorrente.parcelas.length} parcelas
                </p>
              </div>
            )}

            <div className="space-y-3">
              {(parcelasExpandidas || cobrancaRecorrente.parcelas.length <= 3 
                ? cobrancaRecorrente.parcelas 
                : cobrancaRecorrente.parcelas.slice(0, 3)
              ).map((parcela, index) => {
                const status = calcularStatusParcela(parcela);
                const podePagar = podePagarParcela(parcela);
                
                return (
                  <div 
                    key={index} 
                    className="border-l-4 rounded-lg p-4 bg-gray-50"
                    style={{ borderLeftColor: getStatusColor(status) }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">Parcela {parcela.numero}</h4>
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ 
                          backgroundColor: `${getStatusColor(status)}20`,
                          color: getStatusColor(status)
                        }}
                      >
                        {getStatusText(status)}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">
                          Vencimento: {formatarData(parcela.dataVencimento)}
                        </p>
                        {parcela.dataPagamento && (
                          <p className="text-sm text-green-600">
                            Paga em: {formatarData(parcela.dataPagamento)}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-lg font-bold text-gray-900 mb-2">
                          {formatarMoeda(parcela.valor)}
                        </p>
                        {podePagar && (
                          <button
                            onClick={() => handlePagarParcela(parcela)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
                          >
                            Pagar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Seção de Histórico de Pagamentos */}
        {historicoPagamentos.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-bold text-gray-900">
                Histórico de Pagamentos ({historicoPagamentos.length})
              </h3>
            </div>

            <div className="space-y-3">
              {historicoPagamentos.slice(0, 5).map((pagamento, index) => (
                <div 
                  key={index} 
                  className="border-l-4 rounded-lg p-4 bg-gray-50"
                  style={{ 
                    borderLeftColor: pagamento.status === 'pago' ? '#4CAF50' : '#FF9800' 
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-gray-900">
                      Parcela {pagamento.parcelaNumero} • {pagamento.descricao}
                    </p>
                    <span 
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ 
                        backgroundColor: pagamento.status === 'pago' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                        color: pagamento.status === 'pago' ? '#4CAF50' : '#FF9800'
                      }}
                    >
                      {pagamento.status === 'pago' ? 'Pago' : 'Desmarcado'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-1">
                        Vencimento: {formatarData(pagamento.dataVencimento)}
                      </p>
                      <p className="text-sm text-green-600 mb-1">
                        Pago em: {formatarData(pagamento.dataPagamento)}
                      </p>
                      {pagamento.dataDesmarcacao && (
                        <p className="text-sm text-yellow-600">
                          Desmarcado em: {formatarData(pagamento.dataDesmarcacao)}
                        </p>
                      )}
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {formatarMoeda(pagamento.valor)}
                    </p>
                  </div>
                </div>
              ))}
              
              {historicoPagamentos.length > 5 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-600">
                    Mostrando 5 de {historicoPagamentos.length} pagamentos
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Modal de Pagamento */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-bold">Processando Pagamento</h2>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  if (paymentUnsubscribe) {
                    paymentUnsubscribe();
                    setPaymentUnsubscribe(null);
                  }
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {paymentLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Abrindo pagamento...</p>
                  <p className="text-sm text-gray-600">
                    Aguarde enquanto abrimos o pagamento PIX
                  </p>
                </div>
              ) : paymentStatus === 'pending' ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Aguardando Pagamento</p>
                  <p className="text-sm text-gray-600">
                    Aguarde enquanto processamos seu pagamento PIX...
                  </p>
                </div>
              ) : paymentStatus === 'success' ? (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Pagamento Aprovado!</p>
                  <p className="text-sm text-gray-600">
                    Sua parcela foi marcada como paga com sucesso!
                  </p>
                </div>
              ) : paymentStatus === 'error' ? (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Erro no Pagamento</p>
                  <p className="text-sm text-gray-600">
                    Ocorreu um erro durante o processamento do pagamento.
                  </p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 text-blue-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Pagamento PIX</p>
                  <p className="text-sm text-gray-600 mb-4">
                    Se o pagamento não abriu automaticamente, copie o link abaixo:
                  </p>
                  <div className="bg-gray-100 rounded-lg p-3 break-all">
                    <p className="text-xs text-gray-700 font-mono">{paymentUrl}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(paymentUrl);
                      alert('Link copiado para a área de transferência!');
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
                  >
                    Copiar Link
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Menu Lateral */}
      {showMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto border-r border-gray-200/50 transform transition-transform">
            <div className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Menu</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col items-center">
                {empresaFoto ? (
                  <img
                    src={empresaFoto}
                    alt={empresaNome}
                    className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <p className="text-lg font-bold">{empresaNome || 'Empresa'}</p>
                <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
              </div>
            </div>

            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/empresa');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Dashboard</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/status-pedido');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Pedidos</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/perfil');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Perfil</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/mensalidades');
                }}
                className="w-full text-left px-4 py-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Mensalidades</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/relatorio');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Histórico de Entregas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/tabela-taxas');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Tabela de Taxas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/configuracoes');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Configurações</span>
              </button>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span className="font-semibold text-red-600">Sair</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default Mensalidades;

